import { useCallback, useEffect, useRef, useState } from "react";

type RecorderState = {
  /** 録音中か */
  recording: boolean;
  /** 録音済み音声（停止後にセットされる） */
  audioBlob: Blob | null;
  /** ブラウザがサポートしているか */
  supported: boolean;
  /** エラーメッセージ */
  error: string | null;
};

type UseAudioRecorderResult = RecorderState & {
  start: () => Promise<void>;
  stop: () => void;
  reset: () => void;
};

const CANDIDATE_MIME_TYPES = [
  "audio/mp4",
  "audio/webm;codecs=opus",
  "audio/webm",
];

const pickSupportedMimeType = (): string | undefined => {
  if (typeof MediaRecorder === "undefined") return undefined;
  return CANDIDATE_MIME_TYPES.find((type) =>
    MediaRecorder.isTypeSupported(type),
  );
};

const isSupported = (): boolean =>
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== "undefined";

/**
 * MediaRecorder ベースの録音フック。
 *
 * 停止時に確定した Blob を `audioBlob` にセットする（ストリーミングは行わない、単発録音）。
 */
export const useAudioRecorder = (): UseAudioRecorderResult => {
  const [state, setState] = useState<RecorderState>({
    recording: false,
    audioBlob: null,
    supported: isSupported(),
    error: null,
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const releaseStream = useCallback(() => {
    for (const track of streamRef.current?.getTracks() ?? []) {
      track.stop();
    }
    streamRef.current = null;
  }, []);

  const start = useCallback(async () => {
    if (!isSupported()) {
      setState((s) => ({ ...s, supported: false }));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickSupportedMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || mimeType || "audio/webm",
        });
        chunksRef.current = [];
        releaseStream();
        setState((s) => ({ ...s, recording: false, audioBlob: blob }));
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setState((s) => ({
        ...s,
        recording: true,
        error: null,
        audioBlob: null,
      }));
    } catch (e) {
      releaseStream();
      setState((s) => ({
        ...s,
        recording: false,
        error: e instanceof Error ? e.message : "start failed",
      }));
    }
  }, [releaseStream]);

  const stop = useCallback(() => {
    mediaRecorderRef.current?.stop();
  }, []);

  useEffect(() => {
    return () => {
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.onstop = null;
        recorder.stop();
      }
      releaseStream();
    };
  }, [releaseStream]);

  const reset = useCallback(() => {
    setState((s) => ({ ...s, audioBlob: null, error: null }));
  }, []);

  return { ...state, start, stop, reset };
};

/** Blob を base64 文字列（data URL のプレフィックスなし）に変換する */
export const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read blob as data URL"));
        return;
      }
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader error"));
    reader.readAsDataURL(blob);
  });
